<template>
  <!-- ================= UNSUPPORTED PERIOD NOTE ================= -->
  <div v-if="isUnsupported" class="unsupported">
    <div class="unsupported-title">{{ $t('focus.unsupported.title') }}</div>
    <i18n-t keypath="focus.unsupported.body" tag="p">
      <template #period><strong>{{ periodLabel }}</strong></template>
      <template #yesterday><strong>{{ $t('focus.period.1') }}</strong></template>
      <template #last7><strong>{{ $t('focus.period.7') }}</strong></template>
      <template #last30><strong>{{ $t('focus.period.30') }}</strong></template>
    </i18n-t>
    <p class="hint">
      {{ $t('focus.unsupported.hint') }}
    </p>
  </div>

  <!-- ===================== OPPORTUNITY LIST ===================== -->
  <template v-else>
    <!-- Empty state -->
    <div v-if="priorities.length === 0 && !reachShown.length" class="empty">
      {{ $t('focus.empty') }}
    </div>

    <!-- Cards -->
    <div class="pz-grid" v-if="priorities.length">
      <template v-for="(p, idx) in shownPriorities" :key="p.zone">

      <!-- ===== LEAN card — Today's ACT block ("the one thing to do").
           Decision only: zone · € within reach · the recommendation · the
           Guidance link. Compact horizontal layout, no analytical scaffolding. -->
      <article
        v-if="lean"
        class="pz-lean"
        :class="p.patternKind === 'strong' || !(p.potentialImpact > 0) ? 'is-hold' : 'lever-' + leverKindOf(p)"
      >
        <div class="lean-id">
          <div class="lean-zone-row">
            <span class="lean-rank">#{{ idx + 1 }}</span>
            <span class="lean-zone">{{ p.zone }}</span>
            <span v-if="newZones.includes(p.zone)" class="lean-new">{{ $t('focus.new') }}</span>
          </div>
          <span v-if="goalFor(p)" class="lean-goal" :class="{ 'lean-goal--differs': !goalFor(p).matches }">
            <i class="bi bi-flag-fill"></i>
            <template v-if="goalFor(p).matches">{{ $t('focus.yourGoal') }}</template>
            <template v-else>{{ $t('focus.goalDiffers', { goal: goalFor(p).goalLever, data: goalFor(p).dataLever }) }}</template>
          </span>
          <div v-if="p.patternKind !== 'strong' && p.potentialImpact > 0" class="lean-reach">
            <span class="lean-eur">{{ eur(p.potentialImpact) }}</span>
            <span class="lean-reach-lbl">{{ $t('focus.withinReach') }}</span>
          </div>
          <div v-else class="lean-hold">{{ $t('focus.holdLean') }}</div>
        </div>

        <div class="lean-act" v-if="p.patternKind !== 'strong'">
          <template v-if="recFor(p)">
            <span class="lean-lever">
              <i class="bi" :class="leverKindOf(p) === 'conv' ? 'bi-bag-check' : (leverKindOf(p) === 'reach' ? 'bi-signpost-split' : 'bi-hand-index')"></i>
              {{ leverNounOf(p) }}
            </span>
            <p class="lean-action">{{ recFor(p).action }}</p>
          </template>
          <p v-else class="lean-pending">{{ $t('focus.pending') }}</p>
          <a class="rec-link" role="button" tabindex="0"
             @click="$emit('navigate', actTab, p.zone)"
             @keydown.enter="$emit('navigate', actTab, p.zone)">
            {{ $t('focus.act') }}
          </a>
        </div>
      </article>

      <!-- ===== FULL card — deep Prioritization tab (all analytics). -->
      <article
        v-else
        class="pz-card"
        :class="[p.patternClass, p.muted ? 'muted' : '']"
      >
        <!-- Left column: identity (rank + zone + 2 chips + stat strip).
             Drops the previous third "role-pill" — its content is now
             surfaced via the strategic-role tooltip, so a third chip
             would just repeat the same idea. -->
        <header class="pz-card-head">
          <div class="rank">#{{ idx + 1 }}</div>
          <div class="zone">{{ p.zone }}</div>

          <div class="pattern" v-if="!lean">
            <!-- Strategic-role chip (from Intelligence tab's positioning).
                 Hover for the role's meaning + suggested move. -->
            <Tooltip
              v-if="strategicByZone[p.zone]"
              :text="strategicRoleTooltip(strategicByZone[p.zone])"
              placement="bottom"
              align="start"
            >
              <span
                class="strategic-pill"
                :style="{
                  background: mixWhite(strategicByZone[p.zone].color, 0.85),
                  color: strategicByZone[p.zone].color
                }"
              >
                <span class="chip-ico">{{ strategicByZone[p.zone].icon }}</span>
                {{ quadrantText(strategicByZone[p.zone], 'label') }}
              </span>
            </Tooltip>

            <!-- Pattern chip (funnel diagnosis). 'neutral' (Mid-tier) is suppressed: it
                 showed on nearly every card, added no signal, and clashed with the
                 strategic segment chip above. Meaningful gaps + 'strong' still render. -->
            <Tooltip
              v-if="p.patternKind !== 'strong' && p.patternKind !== 'neutral'"
              :text="patternText(p.patternKind, 'legend')"
              placement="bottom"
              align="start"
            >
              <span
                class="pattern-label"
                :style="{
                  background: PATTERN_META[p.patternKind].bg,
                  color: PATTERN_META[p.patternKind].color
                }"
              >
                <span class="chip-ico">{{ PATTERN_META[p.patternKind].icon }}</span>
                {{ patternText(p.patternKind, 'chip') }}
              </span>
            </Tooltip>

            <Tooltip v-else-if="p.patternKind === 'strong'" :text="patternText('strong', 'legend')" placement="bottom" align="start">
              <span
                class="pattern-label pattern-strong"
                :style="{
                  background: PATTERN_META.strong.bg,
                  color: PATTERN_META.strong.color
                }"
              >
                <span class="chip-ico">{{ PATTERN_META.strong.icon }}</span>
                {{ patternText('strong', 'chip') }}
              </span>
            </Tooltip>
          </div>

          <!-- Horizontal stat strip — replaces the 4 metric columns
               (visitors / engage / buy / avg €) that used to live in
               the body grid. Reference data the manager occasionally
               glances at; no need to give each its own column.
               placement="bottom" align="start" keeps it visually
               symmetric with the chips just above (same left-aligned,
               below-the-trigger bubble pattern). -->
          <Tooltip v-if="!lean" :text="$t('focus.refTip')" block placement="bottom" align="start">
            <div class="stat-strip">
            <span>
              <strong>{{ num(p.visitors) }}</strong> {{ $t('focus.stat.visitors') }}
            </span>
            <span class="sep">·</span>
            <span>
              <strong>{{ pct1(p.engagementRate) }}</strong> {{ $t('focus.stat.engage') }}
            </span>
            <span class="sep">·</span>
            <span>
              <strong>{{ pct1(p.conversionRate) }}</strong> {{ $t('focus.stat.buy') }}
            </span>
            <span class="sep">·</span>
            <span>
              <strong>{{ eur(Math.round(p.avgValue)) }}</strong> {{ $t('focus.stat.avg') }}
            </span>
            </div>
          </Tooltip>
        </header>

        <!-- Priority block — the € within reach story.
             Direct sibling of pz-card-head so the card-level grid can
             align all three section titles (zone name / € within
             reach / Small tune-ups) on the same horizontal line. -->
        <div
          class="pz-priority"
          :class="{ 'is-empty': p.patternKind === 'strong' || !(p.potentialImpact > 0) }"
        >
          <div class="priority-head">
            <span class="kpl"><strong>{{ $t('focus.eurWithinReach') }}</strong></span>
          </div>

          <div v-if="p.patternKind === 'strong' || !(p.potentialImpact > 0)" class="priority-empty">
            <span class="priority-amount muted">—</span>
            <span class="muted at-ceiling">{{ $t('focus.holdFull') }}</span>
          </div>

          <div v-else class="priority-body">
            <div class="priority-amount">{{ eur(p.potentialImpact) }}</div>
            <!-- € range honours the estimate uncertainty (wider for conversion). -->
            <div v-if="!lean && p.withinReachRange && p.withinReachRange[1] > 0" class="priority-range muted">
              {{ eur(p.withinReachRange[0]) }}–{{ eur(p.withinReachRange[1]) }}
              <template v-if="p.targetRung"> {{ $t('focus.toRung', { rung: rungLabel(p.targetRung) }) }}</template>
            </div>

            <!-- Focus chip — small CTA, colored by the winning lever
                 (engagement = blue, reach = teal, conversion = amber). -->
            <Tooltip v-if="!lean" :text="leverTip(p)">
              <span class="focus-chip" :class="'lever-' + leverKindOf(p)">
                {{ $t('focus.primaryFocus') }}
                <strong>{{ $t('focus.increasing', { lever: increasingNoun(p) }) }}</strong>
              </span>
            </Tooltip>
          </div>
        </div>

        <!-- Suggested actions (quick wins) — also a direct sibling
             of pz-card-head + pz-priority so the 3-column card grid
             aligns the section titles on the same line. -->
        <!-- What to do — Allen's context-aware recommendation. It's the single
             source of truth; acting on it (track / snooze / dismiss) lives in the
             Guidance tab, which this links to. -->
        <div class="pz-actions">
          <div class="kpl"><strong>{{ $t('focus.whatToDo') }}</strong></div>
          <template v-if="p.patternKind !== 'strong'">
            <div v-if="recFor(p)" class="rec-card" :class="'rec-' + leverKindOf(p)">
              <span class="rec-lever">
                <i class="bi" :class="leverKindOf(p) === 'conv' ? 'bi-bag-check' : (leverKindOf(p) === 'reach' ? 'bi-signpost-split' : 'bi-hand-index')"></i>
                {{ leverNounOf(p) }}
              </span>
              <p class="rec-action">{{ recFor(p).action }}</p>
              <p v-if="recFor(p).why" class="rec-why">{{ recFor(p).why }}</p>
            </div>
            <p v-else class="rec-pending">
              {{ $t('focus.pending') }}
            </p>
            <a class="rec-link" role="button" tabindex="0"
               @click="$emit('navigate', actTab, p.zone)"
               @keydown.enter="$emit('navigate', actTab, p.zone)">
              {{ $t('focus.act') }}
            </a>
          </template>
          <div v-else class="kpv noopp muted">{{ $t('focus.noOpp') }}</div>
        </div>

        <!-- Traffic-lever footer. Spans all three columns of the
             card grid (grid-column: 1 / -1). Sits BELOW the primary
             focus + actions because traffic is a different question
             ("guide more people INTO the zone") than what the
             primary focus addresses ("convert better at current
             traffic"). No actions surfaced here on purpose — would
             be the same generic suggestions on every card, no
             added value. -->
        <div v-if="!lean && p.trafficShare !== null" class="pz-traffic-footer">
          <!-- Column 1: traffic share. Visually prominent number so
               the manager spots zones with very low / very high
               share at a glance, but smaller than the "€ within
               reach" headline so the primary lever stays primary. -->
          <div class="traffic-share">
            <div class="ts-pct">{{ pct1(p.trafficShare) }}</div>
            <div class="ts-pct-label">{{ $t('focus.ofStoreTraffic') }}</div>
          </div>

          <!-- Column 2: aligned with "€ within reach" column so the
               two headers (and their values) sit on the same vertical
               grid line. Header uses the SAME .kpl style as the
               priority block above. -->
          <div v-if="p.trafficSensitivity" class="traffic-sensitivity">
            <div class="ts-head">
              <span class="kpl"><strong>{{ $t('focus.trafficSensitivity') }}</strong></span>
              <span class="ts-hint">{{ $t('focus.atCurrent') }}</span>
            </div>
            <div class="ts-rows">
              <span class="ts-row">
                {{ $t('focus.plusVisitors', { pct: 10 }) }} &rarr;
                <strong>{{ eur(p.trafficSensitivity.pct10) }}</strong>
              </span>
              <span class="ts-row">
                {{ $t('focus.plusVisitors', { pct: 25 }) }} &rarr;
                <strong>{{ eur(p.trafficSensitivity.pct25) }}</strong>
              </span>
              <span class="ts-row">
                {{ $t('focus.plusVisitors', { pct: 50 }) }} &rarr;
                <strong>{{ eur(p.trafficSensitivity.pct50) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </article>

      </template>
    </div>

    <!-- ===== Tripwire reach (Today only) — a zone that only COUNTS arrivals has no
         engagement or conversion, so its opportunity is in VISITORS, not €. Its own
         block, never ranked against the € cards above (a visitor is not a euro). -->
    <section v-if="reachShown.length" class="pz-reach" :class="{ 'pz-reach--alone': !priorities.length }">
      <header class="reach-head">
        <span class="reach-dot"></span>
        <span class="reach-title">{{ $t('focus.reach.title') }}</span>
        <span class="reach-basis">{{ $t('focus.reach.basis') }}</span>
      </header>
      <div class="pz-grid">
        <article v-for="p in reachShown" :key="p.zone" class="pz-lean lever-reach">
          <div class="lean-id">
            <div class="lean-zone-row">
              <span class="lean-zone">{{ p.zone }}</span>
              <span v-if="newZones.includes(p.zone)" class="lean-new">{{ $t('focus.new') }}</span>
            </div>
            <span v-if="goalFor(p)" class="lean-goal" :class="{ 'lean-goal--differs': !goalFor(p).matches }">
              <i class="bi bi-flag-fill"></i>
              <template v-if="goalFor(p).matches">{{ $t('focus.yourGoal') }}</template>
              <template v-else>{{ $t('focus.goalDiffers', { goal: goalFor(p).goalLever, data: goalFor(p).dataLever }) }}</template>
            </span>
            <div class="lean-reach">
              <span class="lean-eur">+{{ num(Math.round(p.withinReachMagnitude)) }}</span>
              <span class="lean-reach-lbl">{{ $t('focus.reach.perMonth') }}</span>
            </div>
          </div>
          <div class="lean-act">
            <span class="lean-lever"><i class="bi bi-signpost-split"></i> {{ $t('focus.reach.lever') }}</span>
            <p v-if="recFor(p)" class="lean-action">{{ recFor(p).action }}</p>
            <p v-if="reachBasisLine" class="lean-basis">{{ reachBasis(p) }}</p>
            <a class="rec-link" role="button" tabindex="0"
               @click="$emit('navigate', actTab, p.zone)"
               @keydown.enter="$emit('navigate', actTab, p.zone)">
              {{ $t('focus.act') }}
            </a>
          </div>
        </article>
      </div>
    </section>
  </template>
</template>

<script>
import { defineComponent, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { QUADRANT_META } from '@/prioritization/strategicPositioning'
import Tooltip from '@/components/ui/Tooltip.vue'
import { formatNumber, formatEuro, formatPercent } from '@/i18n/format'
import { PATTERN_META } from '@/prioritization/settings'
import { computePriorities } from '@/prioritization/priorities'

export default defineComponent({
  name: 'PrioritizationOpportunities',
  components: { Tooltip },
  props: {
    fixedWidgetsData: { type: Object, required: true },
    fixedWidgetsCompareToData: { type: Object, default: null },
    selectedDuration: { type: Number, default: 30 },
    isActive: { type: Boolean, default: true }, // kept for future use
    // Cross-chart context from the Intelligence tab's strategic-positioning
    // composer: { [zoneName]: { icon, label, color } }. Optional — when
    // empty, no strategic-role chip is rendered on the card.
    strategicByZone: { type: Object, default: () => ({}) },
    // Company-wide retail sector + per-zone overrides for ceiling
    // resolution. Empty/'default' fall back to DEFAULT_SECTOR in
    // ceilingsRef. Forwarded into computePriorities so each ranked
    // opportunity is judged against its zone's resolved ceiling.
    segmentKey: { type: String, default: 'default' },
    zoneOverrides: { type: Object, default: () => ({}) },
    // Allen's per-zone recommendation {zone: {action, why}} — injected by the
    // parent (fetched in production, hard-coded in the demo) so this component
    // stays presentational + demo-safe.
    recommendations: { type: Object, default: () => ({}) },
    // Cap the number of cards shown (0 = all). Today's "one thing to do" uses 3.
    limit: { type: Number, default: 0 },
    // Lean render for Today's ACT block: strips the analytical scaffolding
    // (strategic/pattern chips, stat strip, € range, focus chip, traffic footer)
    // down to the decision — zone · € within reach · the recommendation · the
    // act link. The deep Prioritization tab renders the full card (lean=false).
    lean: { type: Boolean, default: false },
    // Where the "Act on this" link navigates. The IA reorg unified Prioritization
    // + Guidance into one Actions lifecycle surface, so this defaults there now.
    actTab: { type: String, default: 'actions-layer' },
    // Optional store goals [{ title, lever, zone? }] — when passed, a lean card whose
    // zone a goal names carries a "Your goal" chip, and says so when the goal's lever
    // differs from the one the data points to. Omitted → no chips (unchanged render).
    goals: { type: Array, default: () => [] },
    // Optional zone names whose suggestion is new this week → a small "New" tag on
    // that card (replaces a separate "N new suggestions" flag). Omitted → no tags.
    newZones: { type: Array, default: () => [] },
    // Reach (visitor) cards: show the analyst basis line ("15.2% now, 19.0% in its best
    // weeks — ±1.5 pt swing"). Today v2 turns it off so a reach card is the same design
    // as a € card — a different unit, not a lesser opportunity. Default → unchanged.
    reachBasisLine: { type: Boolean, default: true },
  },
  emits: ['priorities-updated', 'navigate', 'shown-zones'],
  setup(props, { emit }) {
    // English keeps the exact output it always had (toLocaleString / toFixed);
    // Dutch numbers, € and % go through i18n/format.js.
    const { t, te, locale } = useI18n()
    const isNl = () => locale.value === 'nl'
    const num = (v) => (isNl() ? formatNumber(v, { lang: 'nl' }) : Number(v).toLocaleString())
    const eur = (v) => (isNl() ? formatEuro(v, { lang: 'nl' }) : `€${Number(v).toLocaleString()}`)
    const pct1 = (r) => (isNl()
      ? formatPercent((r || 0) * 100, { decimals: 1, lang: 'nl' })
      : `${((r || 0) * 100).toFixed(1)}%`)
    // Pattern chip / legend (PATTERN_META's words, said in the reader's language).
    const patternText = (kind, field) => {
      const key = `focus.pattern.${kind}.${field}`
      return te(key, 'en') ? t(key) : (PATTERN_META[kind] || {})[field] || ''
    }
    // Ladder rung names arrive as English data ('your best weeks', …) — shown translated.
    const RUNG_KEY = { 'your best weeks': 'ownWeeks', 'your best zones': 'ownZones',
                       'peer cohort': 'cohort', 'your peers': 'peers', 'best-in-class': 'bic' }
    const rungLabel = (name) => (RUNG_KEY[name] ? t(`focus.rung.${RUNG_KEY[name]}`) : name)
    /* -------------------------- Period labels -------------------------- */
    // 90 is the rolling-quarter window introduced for self-calibration
    // (see ceilingsRef.js). Including it in SUPPORTED is what lets the
    // Prio panel actually run for that duration instead of showing the
    // "period not supported" message.
    const SUPPORTED = new Set([1, 7, 30, 90])

    const LABELS = [1, 7, 30, 90, 365, 1000, 2000, 3000, 4000]

    const isUnsupported = computed(
      () => !SUPPORTED.has(props.selectedDuration)
    )
    const periodLabel = computed(
      () => (LABELS.includes(props.selectedDuration)
        ? t(`focus.period.${props.selectedDuration}`)
        : t('focus.period.other', { n: props.selectedDuration }))
    )

    /* ------------------------ Small helpers --------------------------- */
    // roleOf/ROLE_META was used by the dropped third "role pill" — kept
    // here as a comment marker; if a future card revision needs it,
    // restore via getRoleOf from settings.

    // Short sector name for use inside prose ("top DIY stores",
    // "top grocery stores"). Default sector falls back to
    // "top-performing" so the sentence doesn't read awkwardly.
    // Lightweight color-mix helper for the strategic-pill background tint.
    // Inline-applied via :style because the chip color is dynamic (one of
    // four QUADRANT_META colors from the Intelligence-tab composer).
    function mixWhite(hex, weight = 0.85) {
      if (!hex || hex[0] !== '#' || hex.length !== 7) return '#fff'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      const mix = (c) => Math.round(c + (255 - c) * weight)
      return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
    }

    // Tooltip text for the strategic-role pill on each Prio card.
    // Surfaces the same "what this category is + what to do" info
    // that the Strategic Positioning chart's legend strip carries
    // in the Intelligence tab — so the manager learns the meaning of
    // "Star / High Runner / Spotlight Candidate / Cold Spot" without
    // having to switch tabs. Joined into one multi-line string;
    // browsers render \n as a line break inside title attributes.
    // A strategic role's label / legend / interpretation in the reader's language.
    // The role arrives with its English texts (strategicPositioning.QUADRANT_META);
    // matched by key or English label to focus.quadrant.<key>. Unknown: as given.
    function quadrantText(role, field) {
      if (!role) return ''
      const key = role.key || Object.values(QUADRANT_META).find((q) => q.label === role.label)?.key
      const path = `focus.quadrant.${key}.${field}`
      return key && role[field] && te(path) ? t(path) : role[field]
    }

    function strategicRoleTooltip(role) {
      if (!role) return ''
      const lines = []
      if (role.legend) lines.push(quadrantText(role, 'legend'))
      if (role.interp) lines.push(quadrantText(role, 'interp'))
      return lines.join('\n\n')
    }

    /* ----------------------- Main computed list ----------------------- */
    // Use the shared pipeline so the Today hero, the Intelligence deep-dive
    // and this panel all rank the same zones with the same logic. The shared
    // helper keeps zones with visitors but no avgValue (so the Intelligence
    // tab can still show their funnel); the Prioritization panel is strictly
    // about rankable € opportunities, so we re-filter here.
    const all = computed(() =>
      computePriorities(
        props.fixedWidgetsData,
        props.fixedWidgetsCompareToData,
        props.segmentKey,
        props.zoneOverrides,
        props.fixedWidgetsData?._cohort ?? null,
        props.selectedDuration,
      )
    )
    // The € list. A tripwire zone's opportunity is in visitors — it never joins it.
    const priorities = computed(() =>
      all.value.filter(z => z.avgValue > 0 && z.withinReachUnit !== 'visitors')
    )
    // Today's tripwire reach block: zones whose within-reach is extra VISITORS.
    const reachShown = computed(() => {
      // Not lean-only. These are the zones whose opportunity is in VISITORS — every
      // counting line, and any tripwire zone in an otherwise polygon store. Keeping
      // them to Today meant they were invisible on the Actions tab: a whole store of
      // them showed nothing at all, and in a mixed store the tripwire zones silently
      // dropped out of the one list that is meant to be the full one.
      // `limit` already does the capping where capping is wanted.
      const list = all.value.filter(z => z.withinReachUnit === 'visitors' && z.withinReachMagnitude >= 1)
      return props.limit > 0 ? list.slice(0, props.limit) : list
    })
    // Why it's an opportunity, in the zone's own terms: reach now vs its own best weeks
    // (a tripwire zone is judged on its own history only) and its normal swing.
    /**
     * WHEN the best weeks were, when that is a different part of the year from now.
     *
     * "Its own best weeks" is silently seasonal: a zone that peaks in coat season is
     * compared with October every July, and the gap gets billed as something the
     * manager failed to do. Naming the months states a fact and claims nothing —
     * no assertion that the zone IS seasonal, and no correction applied, because
     * telling a season from a good fortnight takes years of history no store here
     * has yet. A reader seeing "its best weeks were in October and November" in July
     * draws the conclusion themselves in a second.
     *
     * Silent when the peak includes the current month: there is nothing to warn
     * about when you are being compared with now.
     */
    const peakWhen = (p) => {
      const months = (p.reachPeakMonths || []).filter((m) => m >= 1 && m <= 12)
      if (!months.length) return ''
      const thisMonth = new Date().getMonth() + 1
      if (months.includes(thisMonth)) return ''
      const names = months.map((m) => t(`focus.months.${m}`))
      const when = names.length === 1 ? names[0]
        : `${names.slice(0, -1).join(', ')} ${t('focus.and')} ${names[names.length - 1]}`
      return ` ${t('focus.reach.peak', { when })}`
    }
    const reachBasis = (p) => {
      const leg = p.ladder?.legs?.reach
      const now = t('focus.reach.now', { p: pct1(p.reachRate || 0) })
      if (!leg || leg.active == null) return `${now}.`
      const own = leg.activeName === 'your best weeks'
      const best = own ? t('focus.reach.inBest') : t('focus.reach.atRung', { rung: rungLabel(leg.activeName) })
      const wobble = (p.reachWobble * 100).toFixed(1)
      const swing = p.reachWobble
        ? ` ${t('focus.reach.swing', { x: isNl() ? wobble.replace('.', ',') : wobble })}` : ''
      return `${now}, ${pct1(leg.active)} ${best}${swing}.${own ? peakWhen(p) : ''}`
    }

    // Today's "one thing to do" caps to the top N; full list (Actions) is uncapped.
    const shownPriorities = computed(() =>
      props.limit > 0 ? priorities.value.slice(0, props.limit) : priorities.value
    )

    // The zones that actually got a card (€ cards + reach cards) — lets a host tell
    // which goals already show on a card and which have none.
    watch(
      () => [...shownPriorities.value.map((p) => p.zone), ...reachShown.value.map((p) => p.zone)],
      (zones) => emit('shown-zones', zones),
      { immediate: true },
    )

    // Emit priorities to parent for use in charts
    watch(
      priorities,
      (val) => {
        emit('priorities-updated', val || [])
      },
      { immediate: true, deep: true }
    )

    /* ------------- Allen's recommendation (Guidance owns acting on it) ------------- */
    // Prefer the ladder's emitted leverKind ('eng' | 'conv' | 'reach' | 'hold');
    // fall back to bestLever for any pre-ladder payload.
    const leverKindOf = (p) =>
      p.leverKind || (p.bestLever === 'Increase Conversion' ? 'conv' : 'eng')
    const LEVER_KEYS = ['eng', 'conv', 'reach']
    const leverKey = (kind) => (LEVER_KEYS.includes(kind) ? kind : 'eng')
    const leverNounOf = (p) => t(`focus.lever.${leverKey(leverKindOf(p))}`)
    // "Increasing Engagement" (English keeps its capital) / "Meer engagement".
    const increasingNoun = (p) => (isNl()
      ? t(`focus.leverLower.${leverKey(leverKindOf(p))}`) : leverNounOf(p))
    // The focus chip's tooltip. English: the explainer priorities.js builds, as always.
    // Dutch: the same sentence rebuilt from the card's own fields.
    const leverTip = (p) => {
      if (!isNl()) return p.leverExplainer
      if (!p.targetRung || !LEVER_KEYS.includes(p.leverKind)) return t('focus.holdTip')
      const m = String(p.leverShort || '').match(/\+?(\d+(?:\.\d+)?)pp/i)
      const pp = m ? m[1].replace('.', ',') : ''
      const est = p.withinReachEst ? ` ${t('today.est')}` : ''
      return t('focus.explainer', { pp, lever: t(`focus.leverLower.${p.leverKind}`), rung: rungLabel(p.targetRung) }) + est
    }
    const recFor = (p) => props.recommendations[p.zone] || null

    // The goal (if any) that targets this card's zone, and whether it pulls on the
    // same lever the data points to. Goal levers are spelled out ('engagement'),
    // card levers are short ('eng') — normalise before comparing.
    const GOAL_LEVER_KIND = { engagement: 'eng', conversion: 'conv', reach: 'reach' }
    const goalFor = (p) => {
      const g = (props.goals || []).find((x) =>
        (x.zone && x.zone === p.zone) || (!x.zone && String(x.title || '').includes(p.zone)))
      if (!g) return null
      const goalKind = GOAL_LEVER_KIND[g.lever] || null
      const kind = leverKindOf(p)
      return {
        matches: !goalKind || goalKind === kind,
        goalLever: GOAL_LEVER_KIND[g.lever] ? t(`focus.leverLower.${GOAL_LEVER_KIND[g.lever]}`) : g.lever,
        dataLever: t(`focus.leverLower.${leverKey(kind)}`),
      }
    }

    return {
      recFor,
      goalFor,
      leverKindOf,
      leverNounOf,
      increasingNoun,
      leverTip,
      patternText,
      rungLabel,
      num,
      eur,
      pct1,
      PATTERN_META,
      // UI
      isUnsupported,
      periodLabel,
      // data
      priorities,
      shownPriorities,
      reachShown,
      reachBasis,
      // helpers
      mixWhite,
      strategicRoleTooltip,
      quadrantText,
    }
  }
})
</script>

<style scoped>
/* Unsupported period note */
.unsupported { border:1px solid #ffe6cc; background:#fff8f0; color:#8a5a00; border-radius:12px; padding:14px 16px; margin-top:6px; }
.unsupported-title { font-weight:700; margin-bottom:6px; }

.empty { padding:18px; background:#f7f9fb; border:1px dashed #d9e3f2; border-radius:10px; color:#8596a7; }

/* ============================================================
   CARD LIST
============================================================ */
.pz-grid { display:flex; flex-direction:column; gap:12px; }

/* ============================================================
   LEAN card — Today's ACT block. Compact 2-column: identity + €
   on the left, the one recommendation on the right. A thin
   lever-colored left edge is the only ornament; no pattern tints,
   no chips, no traffic footer. ~1/3 the height of the full card.
============================================================ */
.pz-lean {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
  gap: 24px;
  align-items: center;
  background: #fff;
  border: 1px solid #e8eef7;
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,.03);
  box-sizing: border-box;
}
.pz-lean.lever-conv  { border-left: 3px solid #2c7a3f; }
.pz-lean.lever-eng   { border-left: 3px solid #4F7EB3; }
.pz-lean.lever-reach { border-left: 3px solid #3E8C80; }
.pz-lean.is-hold     { border-left: 3px solid #d0d7de; opacity: .8; }

.lean-id { display: flex; flex-direction: column; gap: 7px; }
.lean-zone-row { display: flex; align-items: baseline; gap: 8px; }
.lean-rank { font-weight: 700; color: #6B92C6; font-size: 13px; }
.lean-zone { font-weight: 700; color: #2b3a46; font-size: 15px; }
.lean-reach { display: flex; align-items: baseline; gap: 8px; }
.lean-eur {
  font-size: 22px; font-weight: 800; color: #2b3a46;
  letter-spacing: -0.3px; line-height: 1.05;
  font-variant-numeric: tabular-nums;
}
.lean-reach-lbl { font-size: 12px; color: #7a8895; }
.lean-hold { font-size: 12.5px; color: #9aa7b3; }
.lean-goal {
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; line-height: 1.3;
  color: #5d6b2a; background: #f3f6e6; border: 1px solid #e2e9c4;
  border-radius: 999px; padding: 2px 9px;
}
.lean-goal .bi { font-size: 9.5px; }
.lean-new {
  font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: #fff; background: #4F7EB3; border-radius: 999px; padding: 1px 7px; align-self: center;
}
.lean-goal--differs { color: #8a5a00; background: #fff8ec; border-color: #f1dcb0; }

.lean-act { display: flex; flex-direction: column; gap: 3px; }
.lean-lever {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
}
.lean-lever .bi { font-size: .8rem; }
.pz-lean.lever-conv  .lean-lever { color: #2c7a3f; }
.pz-lean.lever-eng   .lean-lever { color: #4F7EB3; }
.pz-lean.lever-reach .lean-lever { color: #3E8C80; }
.lean-action { font-size: .9rem; color: #2b3a46; line-height: 1.45; margin: 2px 0 0; }
.lean-pending { font-size: .82rem; color: #9aa8b3; font-style: italic; margin: 0; }
.lean-act .rec-link { margin-top: 6px; }

@media (max-width: 640px) {
  .pz-lean { grid-template-columns: 1fr; gap: 12px; }
}

/* Tripwire reach block — a sibling of the € cards, never in their ranking. */
.pz-reach { margin-top: 18px; }
.pz-reach--alone { margin-top: 0; }
.reach-head { display: flex; align-items: center; gap: 7px; margin: 0 0 10px; }
.reach-dot { width: 8px; height: 8px; border-radius: 50%; background: #3E8C80; }
.reach-title { font-size: .86rem; font-weight: 700; color: #2b3a46; }
.reach-basis { font-size: .8rem; color: #7a8895; }
.pz-reach .lean-eur { color: #2f6f65; }
.lean-basis { font-size: .8rem; color: #6b7a87; line-height: 1.4; margin: 2px 0 0; }

/* Card = three top-aligned blocks: identity (head) | priority | actions.
   Flattened from the previous nested-grid layout so the three section
   titles (zone name / "€ within reach" / "Small tune-ups") share the
   same row at the top of the card body. Card max-width prevents the
   card from stretching on wide screens — the action column doesn't
   need to consume that real estate. */
.pz-card {
  display: grid;
  /* Floors are 0 (not 320/240/260) so the card can shrink to fit a narrow
     column instead of imposing an ~860px hard minimum that pushed sibling
     widgets (gauges, hero) past the viewport. The max caps keep the exact
     desktop proportions. */
  grid-template-columns:
    minmax(0, 380px)    /* head: zone + chips + stat strip */
    minmax(0, 280px)    /* priority: € + focus chip */
    minmax(0, 320px);   /* actions: vertical pill stack — capped */
  gap: 0 20px;
  width: 100%;
  max-width: 1080px;
  align-items: start;
  border:1px solid #e8eef7; border-radius:14px; background:#fff; padding:12px;
  box-shadow:0 3px 14px rgba(0,0,0,.03); box-sizing:border-box;
  /* overflow:hidden removed — was clipping <Tooltip> bubbles on the
     top-of-card chips (strategic role, pattern, "performing well").
     Border-radius still clips visually because the card has solid
     bg + border; the corner-cut only matters if we add inner
     elements that extend past the radius (none do today). */
}
/* Every column must be allowed to shrink below its content width, otherwise
   a long zone name / chip row re-imposes a min-width on the card. */
.pz-card > * { min-width: 0; }

/* Stack the card + footer into a single column. Triggered at 1080 (not 900):
   the quick-win pills are white-space:nowrap, so once the 3rd grid column
   shrinks below their natural width (~1015px viewport with the sidebar) they
   clip on the right. Stacking before that gives every pill its full row. */
@media (max-width: 1080px){
  .pz-card, .pz-traffic-footer { grid-template-columns: 1fr; }
  .pz-card-head { border-right:0; padding-right:0; }   /* divider makes no sense when stacked */
}

.pz-card-head { display:flex; flex-direction:column; gap:8px; padding-right:16px; border-right:1px solid #eef2f8; }

/* Traffic-lever footer — spans all 3 card columns. Grid columns
   mirror the parent .pz-card grid so:
   - "21.4% of store traffic" sits under the zone-name column
   - "Traffic Sensitivity" sits under the "€ within reach" column
   The third column is intentionally empty (no traffic actions —
   would be the same generic recommendations on every card). */
.pz-traffic-footer {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns:
    minmax(0, 380px)    /* matches pz-card-head */
    minmax(0, 280px)    /* matches pz-priority — "€ within reach" */
    minmax(0, 320px);   /* matches pz-actions — left empty */
  gap: 0 20px;
  align-items: start;
  margin-top: 14px;
  padding-top: 12px;
  color: #6b7a87;
  line-height: 1.4;
}

/* Column 1 — traffic share. Headline pct uses the same size as
   .priority-amount so the two numbers read as siblings. Slightly
   muted color so "€ within reach" still wins as the primary focus. */
.traffic-share {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ts-pct {
  font-size: 22px;
  font-weight: 800;
  color: #4F7EB3;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.ts-pct-label {
  font-size: 12px;
  color: #6b7a87;
}

/* Column 2 — traffic sensitivity. Header mirrors the .priority-head
   styling above so "Traffic Sensitivity" reads as a peer label to
   "€ within reach". */
.traffic-sensitivity {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ts-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 6px;
}
.ts-hint {
  font-size: 11px;
  font-style: italic;
  color: #9aa7b3;
}
.ts-rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #2b3a46;
  font-variant-numeric: tabular-nums;
}
.ts-row strong {
  color: #4F7EB3;
  font-weight: 700;
}

/* Push priority + actions down so their section titles align with
   the ZONE NAME (which is row 2 of the head, under the #1 rank).
   The rank row is ~22px tall (default font line-height) + 8px gap
   in the head's flex column → ~30px offset to align titles. */
.pz-priority,
.pz-actions {
  padding-top: 30px;
  min-width: 0;
}

.rank { font-weight:700; color:#6B92C6; }
.zone { font-weight:600; color:#425461; }

/* Chips (pattern + strategic) — role-pill dropped when the third
   pill was retired; styles for .role-pill removed below. */
.pattern-label, .strategic-pill {
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:999px;
  font-size:12px; font-weight:600; line-height:1.4; /* prevents clipped descenders like “g” */
  cursor:default; user-select:none;
  max-width:240px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.chip-ico { flex:0 0 auto; filter:saturate(0.9); }

/* Strategic-role pill — borrowed Intelligence-tab vocabulary. Colors are
   applied inline because the four QUADRANT_META hues are dynamic. The pill
   sits BEFORE the pattern chip so the card reads role → diagnosis → action. */
.strategic-pill {
  border: 1px solid currentColor;
  /* currentColor would give a too-saturated border at full opacity;
     :style inline already sets `color` to the quadrant color, so we
     dial back the border via opacity. */
  border-color: color-mix(in srgb, currentColor 40%, transparent);
}

/* Pattern chip colors */
.pz-card.pat-awareness .pattern-label { background:#fff0d0; color:#9a6b00; }
.pz-card.pat-attention .pattern-label { background:#fff0f0; color:#9a2727; }
.pz-card.pat-reach     .pattern-label { background:#EAF7F7; color:#2E8B8E; }
.pz-card.pat-neutral   .pattern-label { background:#eef2ff; color:#355b8c; }
.pattern-strong { background:#f8fafc; }

/* Card background tints */
.pz-card.pat-awareness { border-color:#ffe5b4; background:#fff9f1; }
.pz-card.pat-attention { border-color:#ffc4c4; background:#fff4f4; }
.pz-card.pat-reach     { border-color:#BFE9E9; background:#F3FBFB; }
.pz-card.pat-neutral   { border-color:#e8eef7; background:#f6f8ff; }

/* Strong performer muted */
.pz-card.pat-strong { border-color:#e0e6ec; background:#f8fafc; opacity:.75; transition:opacity .2s ease; }
.pz-card.pat-strong:hover { opacity:1; }

/* Keep actionable cards steady on hover */
.pz-card.pat-awareness:hover { background:#fff9f1; opacity:1; }
.pz-card.pat-attention:hover { background:#fff4f4; opacity:1; }
.pz-card.pat-reach:hover     { background:#f4fbf5; opacity:1; }
.pz-card.pat-neutral:hover   { background:#f6f8ff; opacity:1; }

.pz-card .num, .pz-card .kpv { text-align:left; }
.pz-card.muted { opacity:.65; }

.kpl { font-size:11px; color:#7a8895; }
.kpv { font-weight:700; color:#2e3b46; }
.num { font-variant-numeric: tabular-nums; font-feature-settings:'tnum' 1; }
.kpl .muted { color:#7a8895; }

/* Horizontal stat strip — visitors · engage · buy · avg €. Replaces
   the 4 metric columns the old grid used. Compact, reads as a
   single reference-data sentence under the chips. */
.stat-strip {
  font-size: 11.5px;
  color: #6b7a87;
  line-height: 1.5;
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
}
.stat-strip strong {
  color: #2e3b46;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.stat-strip .sep {
  color: #c4ccd6;
  font-weight: 400;
}

/* Priority block — the € within reach story. The block is the
   visual hero of the card; trend chip floats top-right next to the
   section title (no more dedicated trend column).
   Note: padding-top is set higher up so the section title aligns
   with the zone name on the left. */
.pz-priority {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.priority-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.priority-body { display:flex; flex-direction:column; }
.priority-amount {
  font-size: 22px;
  font-weight: 800;
  color: #2b3a46;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  /* extra room under the hero € so the aspirational line below
     doesn't crowd it */
  margin-bottom: 6px;
}
.priority-amount.muted { color:#9aa7b3; font-weight:700; }
.priority-empty { display:flex; flex-direction:column; gap:4px; }
.at-ceiling { font-size:12px; }

/* Focus chip — CTA-style chip below the priority block.
   "Primary focus on: <lever>" wording, with the lever name in
   bold and the chip border/background colored by lever (engagement
   = blue, conversion = amber). margin-top gives the chip its
   "floating" breathing room per the user's call. */
.focus-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 5px 12px;
  margin-top: 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: help;
  user-select: none;
  white-space: normal;
  max-width: 100%;
}
.focus-chip strong { font-weight: 700; }
.focus-chip.lever-eng  { background:#EAF2FA; color:#4F7EB3; border:1px solid #cfdef0; }
.focus-chip.lever-conv { background:#F5F6E0; color:#7C8A1F; border:1px solid #e0e5b8; }
.focus-chip.lever-reach{ background:#E6F4F1; color:#3E8C80; border:1px solid #c7e6df; }
.pz-card.muted .focus-chip.lever-eng  { color:#6B92C6; }
.pz-card.muted .focus-chip.lever-conv { color:#9FB45A; }
.pz-card.muted .focus-chip.lever-reach{ color:#6FB3A8; }
.priority-range { font-size: 11px; margin-top: 2px; }

/* Empty-state for at-ceiling zones (no within-reach lever). */
.is-empty { color:#9aa7b3; }
.is-empty .kpl { color:#7a8895; }

/* Actions block — stacks the "What to do" title over Allen's recommendation. */
.pz-actions {
  display: flex;
  flex-direction: column;
}

/* Allen's recommendation (the "what to do"), with a link to act on it in Guidance */
.rec-card {
  margin-top:8px; padding:10px 12px; border-radius:10px;
  background:#f7faff; border:1px solid #e3ebf6; border-left:3px solid #4F7EB3;
}
.rec-card.rec-conv { background:#f5fbf6; border-color:#dcefe0; border-left-color:#2c7a3f; }
.rec-card.rec-reach { background:#f2faf8; border-color:#d5ece6; border-left-color:#3E8C80; }
.rec-lever {
  display:inline-flex; align-items:center; gap:5px;
  font-size:.68rem; font-weight:800; letter-spacing:.03em; text-transform:uppercase;
  color:#4F7EB3; margin-bottom:5px;
}
.rec-card.rec-conv .rec-lever { color:#2c7a3f; }
.rec-card.rec-reach .rec-lever { color:#3E8C80; }
.rec-lever .bi { font-size:.8rem; }
.rec-action { font-size:.86rem; color:#2b3a45; line-height:1.45; margin:0 0 4px; }
.rec-why { font-size:.8rem; color:#5a6b78; line-height:1.4; margin:0; }
.rec-pending { font-size:.8rem; color:#9aa8b3; font-style:italic; margin:8px 0 0; }
.rec-link {
  display:inline-block; margin-top:8px; font-size:.8rem; font-weight:600;
  color:#4F7EB3; text-decoration:underline; cursor:pointer;
}
.rec-link:hover { color:#365d86; }

</style>